import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CircularProgress } from '@mui/material';
import { useTranslation } from '../hooks/useTranslation';
import { useUser } from '../contexts/UserContext';
import PrivacyDataSection from '../components/PrivacyDataSection';

interface UserInfo {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { user, updateUser, loading: userLoading } = useUser();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
    }
  }, [user]);

  const validateNewPassword = (password: string) => {
    return password.length >= 7 && /[A-Z]/.test(password) && /\d/.test(password);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (newPassword) {
      if (!currentPassword) {
        setError(t('profile.current_password_required'));
        return;
      }
      if (!validateNewPassword(newPassword)) {
        setError(t('validation.password_requirements'));
        return;
      }
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/profile', {
        firstName,
        lastName,
        currentPassword: currentPassword || undefined,
        newPassword: newPassword || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage(t('success.profile_updated'));
      setCurrentPassword('');
      setNewPassword('');
      // Обновляем данные пользователя в контексте
      updateUser({ firstName, lastName });
    } catch (err: any) {
      setError(t('errors.failed_update_profile'));
    } finally {
      setLoading(false);
    }
  };

  if (userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={60} sx={{ color: '#a855f7' }} />
          <p className="text-white mt-4">{t('profile.loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg">{t('errors.failed_load_user_data')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-gray-800/50 backdrop-blur-lg border border-gray-700 rounded-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">
              {t('profile.title')}
            </h1>
            <p className="text-gray-400">{t('profile.subtitle')}</p>
          </div>
          
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-md mb-6">
              {error}
            </div>
          )}
          
          {message && (
            <div className="bg-green-500/20 border border-green-500/50 text-green-300 px-4 py-3 rounded-md mb-6">
              {message}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('auth.email_label')}
              </label>
              <input
                type="email"
                value={user.email}
                disabled
                className="w-full px-4 py-3 bg-gray-700/30 border border-gray-600 rounded-md text-gray-400 cursor-not-allowed"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('registration.first_name_label')}
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('registration.last_name_label')}
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                />
              </div>
            </div>
            
            <div className="border-t border-gray-700 pt-6">
              <h3 className="text-lg font-semibold text-white mb-4">{t('profile.change_password')}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('profile.current_password')}
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                    placeholder={t('profile.current_password_placeholder')}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('profile.new_password')}
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                    placeholder={t('profile.new_password_placeholder')}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {t('validation.password_requirements')}
                  </p>
                </div>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold py-3 px-4 rounded-md transition-all duration-300 disabled:cursor-not-allowed"
            >
              {loading ? t('profile.saving') : t('profile.save_changes')}
            </button>
          </form>

          {/* GDPR Privacy & Data Management Section */}
          <PrivacyDataSection user={user} />
        </div>
      </div>
    </div>
  );
};

export default Profile; 